import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEBUG } from '../utils/debug';

export interface NotificationPayload {
  id: string;
  title: string;
  body: string;
  icon?: string;
  read?: boolean;
  timestamp?: number;
  fromUid?: string;
  fromName?: string | null;
}

interface NotificationState {
  notifications: NotificationPayload[];
  unreadCount: number;
  notificationsEnabled: boolean;
  setNotificationsEnabled: (enabled: boolean) => void;
  addNotification: (notif: NotificationPayload) => void;
  markAllAsRead: () => void;
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  clearNotifications: () => void;
}

const normalizeSenderName = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'anonymous') {
    return undefined;
  }

  return trimmed;
};

const applySenderFallback = (
  body: string,
  senderDisplay: string | undefined,
): string => {
  if (!senderDisplay) {
    return body;
  }

  if (!body) {
    return senderDisplay;
  }

  return body.replace(/anonymous/gi, senderDisplay);
};

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set) => ({
      notifications: [],
      unreadCount: 0,
      notificationsEnabled: false,
      setNotificationsEnabled: (enabled) =>
        set({ notificationsEnabled: enabled }),
      addNotification: (notif: NotificationPayload) =>
        set((state) => {
          const normalizedFromName = normalizeSenderName(notif.fromName);
          const senderDisplay = normalizedFromName ?? notif.fromUid;
          const normalizedBody = applySenderFallback(notif.body ?? '', senderDisplay);
          if (DEBUG) {
            // logging removed
          }
          const newNotif: NotificationPayload = {
            ...notif,
            fromName: normalizedFromName ?? null,
            fromUid: notif.fromUid,
            body: normalizedBody,
            read: false,
            timestamp: Date.now(),
          };
          const updatedNotifications = [newNotif, ...state.notifications];
          const newUnread = updatedNotifications.filter((n) => !n.read).length;
          if (DEBUG) {
            // logging removed
          }
          return {
            notifications: updatedNotifications,
            unreadCount: newUnread,
          };
        }),
      markAllAsRead: () =>
        set((state: NotificationState) => {
          const updated = state.notifications.map((n: NotificationPayload) => ({
            ...n,
            read: true,
          }));
          if (DEBUG) {
            // logging removed
          }
          return {
            notifications: updated,
            unreadCount: 0,
          };
        }),
      markAsRead: (id: string) =>
        set((state: NotificationState) => {
          const updated = state.notifications.map((n: NotificationPayload) =>
            n.id === id ? { ...n, read: true } : n
          );
          const nextUnread = updated.filter((n: NotificationPayload) => !n.read).length;
          if (DEBUG) {
            // logging removed
          }
          return {
            notifications: updated,
            unreadCount: nextUnread,
          };
        }),
      deleteNotification: (id: string) =>
        set((state: NotificationState) => {
          const updated = state.notifications.filter((n: NotificationPayload) => n.id !== id);
          const nextUnread = updated.filter((n: NotificationPayload) => !n.read).length;
          if (DEBUG) {
            // logging removed
          }
          return {
            notifications: updated,
            unreadCount: nextUnread,
          };
        }),
      clearNotifications: () =>
        set(() => {
          if (DEBUG) {
            // logging removed
          }
          return {
            notifications: [],
            unreadCount: 0,
          };
        }),
    }),
    {
      name: 'notifications-enabled',
      partialize: (state) => ({ notificationsEnabled: state.notificationsEnabled }),
    }
  )
);
